import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import styles from "./UserBanner.module.css";
import {Image} from 'react-bootstrap';
import cn from 'classnames';
import { ASSESTS } from '../../../constant/assest';
import CustomLabel, { ILabelTheme } from '../../common/CustomLabel/CustomLabel';
import CustomButton, { ButtonRadiusSize, ButtonType } from '../../common/CustomButton/CustomButton';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import { fetchUserPortfolio, promoteToSponsor } from '../../../redux/slices/portfolio/actions';
import { formatDistanceToNow } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { clearPromotionMessage, clearPromotionError } from '../../../redux/slices/portfolio/slice';
import { UserPortfolio } from '../../../redux/slices/portfolio/types';
import { toast } from 'react-toastify';
import ErrorService from '../../../services/errorService';
import { capitalizedUserName } from '../../../utils';
import { getColorForUser } from '../../common/UserCard/UserCard';
interface IUserBannerProps {
    portfolio: UserPortfolio | null;
    className?: string;
};

const UserBanner: FunctionComponent<IUserBannerProps> = ({ portfolio, className }) => {    
    
    const [showModal, setShowModal] = useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();
    const { promotionLoading, promotionError, promotionMessage } = useSelector((state: RootState) => state.portfolio);

    const bgColor = useMemo(() => getColorForUser( portfolio?.user_full_name || portfolio?.user_username || 'N/A'),
    [portfolio]);
    
    const handlePromote = async () => {
        if (!portfolio) return;

        try {
            await dispatch(promoteToSponsor(portfolio.user_id)).unwrap();
            dispatch(fetchUserPortfolio(portfolio.user_id));
            setShowModal(false);
        } catch (error) {
            console.error('Error promoting to sponsor:', error);          
        }
    };

    useEffect(() => {
        if (promotionMessage) {
            toast.success(promotionMessage);
            dispatch(clearPromotionMessage());
        }
    }, [promotionMessage, dispatch]);

    useEffect(() => {
        if (promotionError) {
            toast.error(ErrorService.getErrorMessage(promotionError));
            dispatch(clearPromotionError());
        }
    }, [promotionError, dispatch]);

    const renderItem = (icon?: string, text?: string) => {
        return (
            <div className={cn(styles.useBannerItem)}>
                {icon &&
                    <div className={cn(styles.useBannerIcon)}>
                        <Image src={icon} width={20} height={20} alt={text} />
                    </div>
                }
                <div className={cn(styles.useBannerText)}>{text}</div>
            </div>
        );
    };

    if (!portfolio) {
        return null;
    }

    const { 
        user_full_name,
        user_username,
        plan_plan_name,
        user_profile_type,
        user_status,
        profile_image,
        profile_cover,
        user_dateOfBirth,
        con_name,
        user_email,
        user_last_login,
        blockedUser,
        referralData,
        user_bio,
        user_is_sponsor,
        rewards,
        user_preferred_language,
    } = portfolio;

    const timeAgo = user_last_login && !isNaN(new Date(user_last_login).getTime())
        ? formatDistanceToNow(new Date(user_last_login), { addSuffix: true })
        : 'N/A';
        
    return (
        <div className={cn(styles.useBannerWrap, className)}>
            <div className={cn(styles.useBannerTop)} style={{backgroundImage: `url(${profile_cover || ASSESTS.BANNERBG})`}}>
                <div className={cn(styles.useBannerCard)}>
                    <div className={cn(styles.useBannerCardImage, !profile_image && styles.userAvatarText)}
                            style={!profile_image ? { backgroundColor: bgColor } : undefined}>
                        {profile_image ? 
                            <Image src={profile_image} width={88} height={88} alt={user_full_name} /> : 
                            (user_full_name ? user_full_name.trim()?.charAt(0).toUpperCase() : '?')
                        }
                    </div>
                    <div className={cn(styles.useBannerCardSummary)}>
                        <div className={cn(styles.useName)}>{capitalizedUserName(user_full_name)}</div>
                        {user_username && <div className={cn(styles.useHandle)}>@{user_username}</div>}
                        <div className={cn(styles.useTags)}>
                            {plan_plan_name && <CustomLabel label={plan_plan_name} theme={ILabelTheme.HARM} />}
                            {user_profile_type && <CustomLabel label={user_profile_type} theme={ILabelTheme.DEFAULT} />}
                            {user_status && <CustomLabel label={user_status} theme={user_status=== 'inactive' ? ILabelTheme.WARN : ILabelTheme.SUCCESS} />}
                        </div>
                    </div>
                </div>
                <div title={user_is_sponsor ? 'User is already a sponsor' : ''}>
                    <CustomButton
                        btnType={ButtonType.GREEN}
                        btnRadius={ButtonRadiusSize.SMALL}
                        className={styles.promoteBtn}
                        onClick={() => setShowModal(true)}
                        disabled={user_is_sponsor}
                    >
                        Promote to sponsor
                    </CustomButton>
                </div>
            </div>
            <div className={cn(styles.useBannerBottom)}>
                <div className={cn(styles.useBannerDetailCol, styles.personalInformation)}>
                    <div className={cn(styles.title)}>Personal Information</div>
                    <div className={styles.detail}>
                        {/* {renderItem(ASSESTS.CALENDER, `Born: ${user_dateOfBirth ? format(new Date(user_dateOfBirth), 'MM-dd-yyyy'): 'N/A'}`)} */}
                        {/* {renderItem(ASSESTS.CALENDER, `Born: ${format(new Date(user_dateOfBirth), 'P') || 'N/A}`)} */}
                        {renderItem(ASSESTS.CALENDER, `Born: ${user_dateOfBirth ? user_dateOfBirth.split('T')[0] : 'N/A'}`)}
                        {renderItem(ASSESTS.LOCATION, con_name || 'N/A')}
                        {renderItem(ASSESTS.EMAIL, user_email || 'N/A')}
                        {renderItem(ASSESTS.LANGUAGE, `Language: ${user_preferred_language || 'English'}`)}
                    </div>
                </div>
                <div className={cn(styles.useBannerDetailCol, styles.accountRewards)}>
                    <div className={cn(styles.title)}>Account & Rewards</div>
                    <div className={styles.detail}>
                        {renderItem(ASSESTS.TIME, `Last Login: ${timeAgo}`)}
                        {renderItem(ASSESTS.BLOCKEDUSER, `Blocked users: ${blockedUser || 'N/A'}`)}
                        {renderItem(ASSESTS.REFERALS, `Referrals: ${referralData || 'N/A'}`)}
                        {renderItem(ASSESTS.REWARDS, `Rewards: ${rewards || 'N/A'}`)}
                    </div>
                </div>
                <div className={cn(styles.useBannerDetailCol, styles.accountBio)}>
                    <div className={cn(styles.title)}>Biography</div>
                    <div className={styles.detail}>
                        <div className={cn(styles.bio)}>
                            {user_bio || 'N/A'}
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationModal 
                title={'Promote to Sponsor'}
                showModal={showModal}
                onClose={() => setShowModal(false)} 
                bodyText={'Are you sure you want to promote this user to a sponsor?'} 
                cancelBtnText='Cancel'
                submitBtnText={promotionLoading === 'pending' ? 'Promoting...' : 'Submit'}
                onSubmit={handlePromote}
                isSubmitDisabled={promotionLoading === 'pending'}
            />
            
        </div>
    );
};

export default React.memo(UserBanner);
